import { useCallback, useEffect, useRef, useState } from 'react';
import { NOME_MODO } from '@core/motor/mensagens';
import type { ModoJogo } from '@core/tipos';
import { Marca } from './Marca';
import { IconeDesfazer } from './TecladoVirtual';

/**
 * Barra superior (§7.1).
 *
 * Quatro controles, e eles cabem numa tela de 320 px sem menu de estouro: o
 * modo em curso, `Desfazer`, `Recomeçar` e a informação.
 *
 * `Desfazer` ganhou botão (§11). Era só `Ctrl+Z` no campo, e `Ctrl+Z` não
 * existe em aparelho de toque nem se descobre sozinho em nenhum: a ação que o
 * jogo passou a oferecer tem de estar visível para ser oferecida de verdade.
 * Fica ao lado de `Recomeçar` porque as duas andam para trás, e com ícones
 * diferentes porque uma volta um passo e a outra apaga tudo.
 *
 * O botão de modo carrega o nome do modo em vez de um rótulo fixo: ele é ao
 * mesmo tempo o indicador de em que jogo se está e a porta para trocá-lo
 * (§22, T10-01). É o único com palavra — os outros são ícone, porque
 * `Recomeçar` é gesto conhecido e o resto não disputa a barra com o jogo.
 *
 * `Como jogar`, os dois downloads e `Sobre` ficam juntos atrás do `i`: nenhum
 * é frequente o bastante para ocupar a barra sozinho, e todos são sobre o jogo
 * em vez de serem jogadas.
 */

/**
 * §7.5: as duas saídas do tabuleiro.
 *
 * Vêm juntas porque aparecem e somem juntas — as duas dependem de haver
 * tabuleiro, e nenhuma faz sentido sem a outra.
 */
export interface DownloadsDoTabuleiro {
  readonly png: () => void;
  readonly gif: () => void;
}
export function BarraSuperior({
  modo,
  aoAbrirModos,
  aoDesfazer,
  aoRecomecar,
  aoAbrirAjuda,
  aoAbrirSobre,
  aoBaixarImagem,
  som,
  aoAlternarSom,
}: {
  modo: ModoJogo;
  aoAbrirModos: () => void;
  /**
   * §11: ausente enquanto não há o que desfazer, e aí o botão fica
   * desabilitado em vez de sumir — sumir seria um controle que muda de lugar
   * conforme a partida anda, como os downloads do painel do `i`.
   */
  aoDesfazer?: (() => void) | undefined;
  /** Opcional porque nem todo modo precisa se recomeçar. */
  aoRecomecar?: () => void;
  aoAbrirAjuda: () => void;
  aoAbrirSobre: () => void;
  /** §7.5: ausente enquanto não há tabuleiro — os itens ficam desabilitados. */
  aoBaixarImagem?: DownloadsDoTabuleiro | undefined;
  /** §24: som ligado? O rótulo diz o estado, não o que o clique fará. */
  som: boolean;
  aoAlternarSom: () => void;
}) {
  const infoRef = useRef<HTMLDetailsElement>(null);
  const [infoAberta, setInfoAberta] = useState(false);

  /* Escolher um item fecha o menu; o diálogo que abre em seguida toma o foco. */
  const pelaInfo = useCallback(
    (acao: () => void) => () => {
      if (infoRef.current !== null) infoRef.current.open = false;
      setInfoAberta(false);
      acao();
    },
    [],
  );

  /**
   * `<details>` não fecha sozinho: sem isto o painel ficaria aberto por cima
   * do tabuleiro depois que a pessoa desistiu dele. `Esc` e o clique fora são
   * as duas saídas que qualquer menu já ensinou a esperar.
   */
  useEffect(() => {
    if (!infoAberta) return;

    const fechar = () => {
      if (infoRef.current !== null) infoRef.current.open = false;
      setInfoAberta(false);
    };
    const aoApontar = (e: PointerEvent) => {
      if (e.target instanceof Node && infoRef.current?.contains(e.target) === true) return;
      fechar();
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      fechar();
      // O foco cairia no `body` e as teclas seguintes se perderiam.
      infoRef.current?.querySelector('summary')?.focus();
    };

    document.addEventListener('pointerdown', aoApontar);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('pointerdown', aoApontar);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [infoAberta]);

  return (
    <header className="barra">
      <Marca />

      <nav className="barra__acoes" aria-label="Ações do jogo">
        <button
          type="button"
          className="botao botao--modo"
          onClick={aoAbrirModos}
          title="Escolher o modo de jogo"
        >
          <span className="apenas-leitor">Modo de jogo: </span>
          {NOME_MODO[modo]}
        </button>

        <button
          type="button"
          className="botao botao--icone"
          onClick={aoDesfazer}
          disabled={aoDesfazer === undefined}
          aria-label="Desfazer"
          title={aoDesfazer === undefined ? 'Nada a desfazer' : 'Desfazer a última escolha'}
        >
          <IconeDesfazer tamanho={28} />
        </button>

        {/* Lixeira: o gesto é "joga isto fora", não "para trás".
            O rótulo acessível continua sendo `Recomeçar`. */}
        {aoRecomecar !== undefined && (
          <button
            type="button"
            className="botao botao--icone"
            onClick={aoRecomecar}
            aria-label="Recomeçar"
            title="Recomeçar"
          >
            <IconeRecomecar />
          </button>
        )}

        <details
          className="info"
          ref={infoRef}
          onToggle={(e) => setInfoAberta(e.currentTarget.open)}
        >
          <summary
            className="botao botao--icone info__abrir"
            aria-label="Ajuda e informações"
            title="Ajuda e informações"
          >
            <IconeInformacao />
          </summary>
          <div className="info__itens">
            <button type="button" className="botao info__item" onClick={pelaInfo(aoAbrirAjuda)}>
              Como jogar
            </button>
            <button
              type="button"
              className="botao info__item"
              onClick={aoBaixarImagem === undefined ? undefined : pelaInfo(aoBaixarImagem.png)}
              disabled={aoBaixarImagem === undefined}
              title={
                aoBaixarImagem === undefined
                  ? 'Escreva uma palavra para ter o que baixar'
                  : 'Baixar o tabuleiro inteiro como imagem'
              }
            >
              Baixar imagem
            </button>
            <button
              type="button"
              className="botao info__item"
              onClick={aoBaixarImagem === undefined ? undefined : pelaInfo(aoBaixarImagem.gif)}
              disabled={aoBaixarImagem === undefined}
              title={
                aoBaixarImagem === undefined
                  ? 'Escreva uma palavra para ter o que baixar'
                  : 'Baixar o tabuleiro sendo digitado, como GIF'
              }
            >
              Baixar GIF
            </button>
            {/* Sem `pelaInfo`: um interruptor que fecha o menu ao ser tocado
                esconde justamente a resposta a "ligou?". */}
            <button
              type="button"
              className="botao info__item"
              onClick={aoAlternarSom}
              aria-pressed={som}
            >
              Som: {som ? 'ligado' : 'desligado'}
            </button>
            <button type="button" className="botao info__item" onClick={pelaInfo(aoAbrirSobre)}>
              Sobre
            </button>
          </div>
        </details>
      </nav>
    </header>
  );
}

/**
 * Lixeira: tampa, alça e corpo com dois riscos dentro.
 *
 * Era um arco anti-horário, e arco é o vocabulário de `Desfazer` (§11): lado a
 * lado na barra, duas setas curvas para a esquerda só se distinguem por quanto
 * fecham, e ninguém mede isso de relance. A confusão sairia caro, porque uma
 * volta um passo e a outra apaga o tabuleiro inteiro — e é justamente isso que
 * a lixeira diz sem palavra nenhuma. Não partilha traço com a seta: uma é
 * curva aberta, a outra é caixa fechada.
 *
 * O corpo afina para baixo (17,2 px de boca contra 13,6 px de fundo) porque
 * lixeira de lados paralelos lê como copo.
 */
function IconeRecomecar() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false">
      <path
        d="M4.4 6.4h15.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M9.6 6.4V4.9a1.15 1.15 0 0 1 1.15-1.15h2.5A1.15 1.15 0 0 1 14.4 4.9v1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M6.5 6.4l.82 12.24a1.6 1.6 0 0 0 1.6 1.49h6.16a1.6 1.6 0 0 0 1.6-1.49L17.5 6.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.2 10.1v6.6M13.8 10.1v6.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconeInformacao() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="7.6" r="1.15" fill="currentColor" />
      <path
        d="M12 10.8v6.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
